import { SearchType } from 'utils/enum';

export const OPENSEARCH_MAX_RESULT_WINDOW = 10000;
// Where semantic browse switches from offset to cursor paging. Must equal the
// engine's SearchConfig.browse_window.
export const SEMANTIC_BROWSE_WINDOW = 10000;

export const DEFAULT_PAGE_SIZE = 30;
export const PAGE_SIZE_OPTIONS = [12, 30, 60, 120];
export const SEMANTIC_PAGE_SIZE_OPTIONS = [12, 30];

export const SEMANTIC_MAX_RESULTS = 200;

export const semanticMaxPage = (pageSize: number): number =>
  pageSize > 0 ? Math.max(1, Math.floor(SEMANTIC_MAX_RESULTS / pageSize)) : 1;

/** Semantic search with no transcript text (blank, or a title search, which
 * the engine turns into a filter) browses the index like /library: no
 * 200-result pool, the library's page sizes, and search_after past 10000. */
export const isSemanticBrowse = (
  searchTerm: string | null | undefined,
  searchBy: string | null | undefined,
): boolean => !searchTerm?.trim() || searchBy === SearchType.TITLE;

export const pageSizeOptionsFor = (
  isSemanticSearch: boolean,
  isBrowse = false,
): number[] =>
  isSemanticSearch && !isBrowse
    ? SEMANTIC_PAGE_SIZE_OPTIONS
    : PAGE_SIZE_OPTIONS;

// page_size is shared by both modes, so a size picked in one mode can be
// missing from the other mode's options.
export const validPageSize = (
  pageSize: number,
  isSemanticSearch: boolean,
  isBrowse = false,
): number =>
  pageSizeOptionsFor(isSemanticSearch, isBrowse).includes(pageSize)
    ? pageSize
    : DEFAULT_PAGE_SIZE;

// The paging a Library fetch should use. The first fetch after a mode switch
// (Clips on or off for a semantic tenant) lands on page 1 of the new mode,
// with a page size that mode offers.
export const resolveModePaging = ({
  isSemanticSearch,
  isBrowse = false,
  fetchedIsSemanticSearch,
  pageNumber,
  pageSize,
}: {
  isSemanticSearch: boolean;
  isBrowse?: boolean;
  fetchedIsSemanticSearch: boolean | null;
  pageNumber: number;
  pageSize: number;
}): { page_number: number; page_size: number } => ({
  page_number:
    fetchedIsSemanticSearch !== null &&
    fetchedIsSemanticSearch !== isSemanticSearch
      ? 1
      : pageNumber,
  page_size: validPageSize(pageSize, isSemanticSearch, isBrowse),
});

export function clampLibraryPage(
  pageNumber: number,
  pageSize: number,
  totalCount: number,
): number {
  if (pageSize <= 0 || totalCount <= 0) {
    return 1;
  }
  const maxPage = Math.max(1, Math.ceil(totalCount / pageSize));
  return Math.min(Math.max(pageNumber, 1), maxPage);
}

export function getMaxOffsetPage(pageSize: number): number {
  if (pageSize <= 0) {
    return 0;
  }
  return Math.floor(OPENSEARCH_MAX_RESULT_WINDOW / pageSize);
}

export function requiresSearchAfter(
  pageNumber: number,
  pageSize: number,
  resultWindow: number = OPENSEARCH_MAX_RESULT_WINDOW,
): boolean {
  return pageSize > 0 && pageNumber * pageSize > resultWindow;
}
